import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { searchAutoCompleteApi } from 'api/search';
import { sendRequest } from 'api/sendRequest';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useToast } from 'components/hooks/useToast';
import { useDispatch } from 'react-redux';

import { OKR_TYPES, SEARCH_WIDGET_TYPES, TOAST_STATUSES } from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';
import { endpoints } from 'constants/endpoints';
import { sendUpdatedKREvent } from 'constants/mixpanelHelper';

import { useOKRDrawerControlCallbacks } from '../useOKRDrawerControlCallbacks';

export const QUERY_KEYS = {
  EMPLOYEE_SEARCH: 'EMPLOYEE_SEARCH',
  KEY_RESULT: 'KEY_RESULT',
  OKR_TOP_HIERARCHY: 'OKR_TOP_HIERARCHY',
  OKR_CHILDREN: 'OKR_CHILDREN',
  OKR_INITIATIVES: 'OKR_INITIATIVES',
  CUSTOM_TAGS: 'CUSTOM_TAGS',
  OKR_CUSTOM_TAGS: 'OKR_CUSTOM_TAGS',
  INITIATIVE_COMMENTS: 'INITIATIVE_COMMENTS',
  GENERATE_TITLE_WITH_AI: 'GENERATE_TITLE_WITH',
};

/**
 * @deprecated 8-04-2025
 */
export const useEmployeeNameAndEmailAutocompleteSearch = ({ text, widgetType = SEARCH_WIDGET_TYPES.task, limit = 5 }) =>
  useQuery([QUERY_KEYS.EMPLOYEE_SEARCH, text, widgetType, limit], () =>
    searchAutoCompleteApi({
      tagType: 'EMPLOYEE_NAME_AND_EMAIL',
      query: text,
      widgetType,
      limit,
    })
  );

export const getKeyResultApi = ({ id }) => {
  const url = endpoints.rabbitGoals.index(id);

  return sendRequest({
    method: 'GET',
    url,
    credentials: 'include',
  });
};

export const useGetKRDetailsQuery = ({ id, enabled }) =>
  useQuery(
    [QUERY_KEYS.KEY_RESULT, id],
    () =>
      getKeyResultApi({
        id,
      }).then((res) => res.entity),
    { enabled, retry: 2 }
  );

const updateKRApi = ({ uuid, ...rest }) => {
  const url = endpoints.rabbitGoals.index(uuid);

  return sendRequest({
    method: 'PUT',
    url,
    body: {
      uuid,
      ...rest,
      /* fix for actual being sent as null */
      actual: rest?.actual ?? 0,
    },
    credentials: 'include',
  });
};

export const useUpdateKRMutation = (uuid, onSuccessCallback = () => {}) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const { onUpdate } = useOKRDrawerControlCallbacks();
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();
  const toast = useToast();

  const queryKey = [QUERY_KEYS.KEY_RESULT, uuid];

  return useMutation({
    mutationFn: ({ updates, previousKR }) => {
      const previousKR2 = queryClient.getQueryData(queryKey);
      const currentValue = JSON.stringify(updates);
      let oldValue = {};
      Object.keys(updates).forEach((key) => {
        oldValue[key] = previousKR2[key];
      });
      oldValue = JSON.stringify(oldValue);
      if (previousKR2?.type === OKR_TYPES.KEY_RESULT) {
        sendUpdatedKREvent({
          eventProperties: { currentValue, oldValue, fieldType: 'all', goalId: uuid },
        });
      }

      // parent can not be updated with this api , in the success callback we are updating the parentPost with realign api
      return updateKRApi({ ...previousKR2, ...updates, parentPost: { ...previousKR2?.parentPost } });
    },
    onMutate: ({ updates }) => {
      queryClient.cancelQueries(queryKey);

      const previousKR = queryClient.getQueryData(queryKey);
      /* 
        removing optimistic updates for now,
        as it triggers graph progress call even before data is updated on be 
      */
      /* queryClient.setQueryData(queryKey, (previousKR) => {
        return { ...previousKR, ...updates };
      }); */
      return { previousKR };
    },
    onError: (err, newKR, context) => {
      /* commenting optimistic update correction */
      /* queryClient.setQueryData(queryKey, context.previousKR); */
      toast({
        title: err.message || `The ${modifyString(moduleName, StringModifier.SENTENCE_CASE)} could not be updated`,
        status: TOAST_STATUSES.ERROR,
        position: 'bottom-left',
      });
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries([QUERY_KEYS.OKR_TOP_HIERARCHY, uuid]);
      setTimeout(() => {
        queryClient.invalidateQueries([QUERY_KEYS.INITIATIVE_COMMENTS]);
      }, 800);
      if (typeof onUpdate === 'function') {
        onUpdate();
      }
      if (typeof onSuccessCallback === 'function') {
        onSuccessCallback();
      }
      queryClient.setQueryData(queryKey, data?.entity);
      if (!variables?.isSuppressSuccessToast) {
        toast({
          title: `Your changes have been saved`,
          status: TOAST_STATUSES.SUCCESS,
          position: 'bottom-left',
        });
      }
    },
  });
};
